<template>
  <el-drawer
    title="Beneficiaries"
    :visible.sync="showDrawer"
    :with-header="true"
    :before-close="handleClose"
    :size="drawerWidth"
  >
    <section class="px-4">
      <!-- search -->
      <div class="relative">
        <el-input v-model="search" placeholder="Search" />
        <search-icon class="absolute top-4 left-5" />
      </div>
      <div class="mt-8">
        <transition name="fade-in" type="group" mode="in-out">
          <div v-if="!noBeneficiaries">
            <div v-for="(beneficiary, index) in beneficiaryList" :key="index">
              <div
                @click="fillBeneficiaryDetails(beneficiary)"
                class="border-main shadow-sm cursor-pointer rounded-lg w-full bg-gray-60 flex flex-col lg:flex-row gap-y-2.5 p-4 mb-3"
              >
                <div
                  v-if="
                    beneficiary.transferDestination !==
                    $transactionType.ASSET_TRANSFER
                  "
                  class="mr-4"
                >
                  <el-avatar
                    :size="size"
                    class="bg-subtle text-primary border-main lg:text-xl"
                    >{{ beneficiary.name.charAt(0).toUpperCase() }}</el-avatar
                  >
                </div>
                <div v-else class="mr-5">
                  <el-avatar
                    :size="size"
                    class="bg-subtle text-primary border-main lg:text-xl"
                    >{{
                      beneficiary.details.walletDetails.walletAddress
                        .charAt(0)
                        .toUpperCase()
                    }}</el-avatar
                  >
                </div>
                <div
                  v-if="
                    beneficiary.transferDestination !==
                    $transactionType.ASSET_TRANSFER
                  "
                  class="pr-12"
                >
                  <p
                    class="text-fg-default text-sm lg:text-base font-bold capitalize"
                  >
                    {{ beneficiary.name }}
                  </p>
                  <p
                    class="text-11px lg:text-13px text-fg-muted font-normal mt-0.5 mb-1"
                  >
                    Bank: {{ beneficiary.details.bankDetails.bankName }}
                  </p>
                  <p
                    class="text-11px lg:text-13px text-fg-muted font-normal mb-1"
                  >
                    Account number:
                    {{ beneficiary.details.bankDetails.accountNumber }}
                  </p>
                  <p class="text-11px lg:text-13px text-fg-muted font-normal">
                    Destination Country:
                    {{ beneficiary.details.bankDetails.country }}
                  </p>
                </div>
                <div v-else>
                  <p
                    class="text-11px lg:text-13px text-fg-muted font-normal mb-1"
                  >
                    Wallet Address:
                    {{
                      maskWalletAddress(
                        beneficiary.details.walletDetails.walletAddress
                      )
                    }}
                  </p>
                  <p
                    class="text-11px lg:text-13px text-fg-muted font-normal mb-1"
                  >
                    Asset Type:
                    {{ beneficiary.details.walletDetails.assetType }}
                  </p>
                  <p class="text-11px lg:text-13px text-fg-muted font-normal">
                    Network:
                    {{
                      parseNetworkName(
                        beneficiary.details.walletDetails.network
                      )
                    }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </transition>
        <transition name="fade-in" type="group" mode="in-out">
          <div
            v-if="noBeneficiaries"
            class="flex flex-col items-center justify-center"
          >
            <empty-wallet />
            <p class="mt-4 font-semibold text-xs lg:text-sm text-primary">
              No Beneficiary Found
            </p>
          </div>
        </transition>
      </div>
    </section>
  </el-drawer>
</template>
<script>
import breakpoints from '@/utils/breakpoints.js'
import activeCurrency from '@/mixins/localDeposit.js'
import country from '@/mixins/getCountryDetails.js'
import { MaskWalletAddressTwo, ParseNetworkName } from '@/utils/helpers.js'

export default {
  name: 'TerminalBeneficiaryDrawer',
  mixins: [activeCurrency, country],
  data () {
    return {
      breakpoints,
      isLoading: false,
      receiveNetwork: '',
      receiveAsset: '',
      search: '',
      payload: {
        page: 1,
        size: 5
      }
    }
  },
  props: {
    showDrawer: {
      type: Boolean,
      required: true
    }
  },
  components: {
    Transition: () =>
      import('@/components/v2/animation/transition/Transition.vue'),
    EmptyWallet: () => import('@/assets/v2/icons/EmptyWallet.vue'),
    SearchIcon: () => import('@/assets/v2/icons/SearchIcon.vue')
  },
  computed: {
    drawerWidth () {
      return breakpoints.lgAndUp ? '35%' : breakpoints.mdAndUp ? '70%' : '90%'
    },
    size () {
      return breakpoints.lgAndUp ? 50 : breakpoints.mdAndUp ? 50 : 40
    },
    beneficiaryList () {
      const isMobileMoneyFlag = this.isMobileMoneyPayoutMethod ? 'yes' : 'no'

      if (this.search) {
        return this.$store.state.terminal.beneficiaries.items.filter(
          (beneficiary) => {
            const isMobileMoneyBeneficiary =
              beneficiary.details.bankDetails?.isMobileMoney || 'no'

            return (
              beneficiary.destinationCurrency === this.secondaryCurrency &&
              (beneficiary.name
                .toLowerCase()
                .includes(this.search.toLowerCase()) ||
                beneficiary.details.bankDetails.accountNumber
                  .toString()
                  .includes(this.search)) &&
              isMobileMoneyBeneficiary === isMobileMoneyFlag
            )
          }
        )
      } else {
        return this.$store.state.terminal.beneficiaries.items.filter(
          (beneficiary) => {
            const isMobileMoneyBeneficiary =
              beneficiary.details.bankDetails?.isMobileMoney || 'no'

            return (
              beneficiary.destinationCurrency === this.secondaryCurrency &&
              isMobileMoneyBeneficiary === isMobileMoneyFlag
            )
          }
        )
      }
    },
    noBeneficiaries () {
      return this.beneficiaryList.length === 0
    }
  },
  methods: {
    fillBeneficiaryDetails (beneficiary) {
      let details = {}
      if (
        beneficiary.transferDestination !== this.$transactionType.ASSET_TRANSFER
      ) {
        details = {
          accountNo: beneficiary.details.bankDetails.accountNumber,
          accountName: beneficiary.details.bankDetails.accountName,
          routingNo: beneficiary.details.bankDetails.routingNumber,
          bankName: beneficiary.details.bankDetails.bankName,
          bankCountry: beneficiary.details.bankDetails.country,
          bankBranch: beneficiary.details.bankDetails.bankBranch,
          bankCity: beneficiary.details.bankDetails.city,
          bankPostalCode: beneficiary.details.bankDetails.postalCode,
          bankCode: beneficiary.details.bankDetails.bankCode,
          bankAddress: beneficiary.details.bankDetails.bankAddress,
          recipientName: beneficiary.details.personalDetails?.name || '',
          recipientAddress: beneficiary.details.personalDetails?.address || '',
          recipientCountry: beneficiary.details.personalDetails?.country || '',
          swiftCode: beneficiary.details.bankDetails.swiftCode,
          intermediaryBankReference:
            beneficiary.details.intermediaryBank?.reference || '',
          intermediaryBankAddress:
            beneficiary.details.intermediaryBank?.bankAddress || '',
          intermediaryBankName:
            beneficiary.details.intermediaryBank?.bankName || '',
          intermediaryBankSwiftCode:
            beneficiary.details.intermediaryBank?.swiftCode || '',
          recipientCity: beneficiary.details.personalDetails?.city || '',
          recipientPostalCode:
            beneficiary.details.personalDetails?.postalCode || ''
        }
      } else {
        details = {
          walletAddress: beneficiary.details.walletDetails.walletAddress || '',
          asset: beneficiary.details.walletDetails?.assetType || '',
          network: beneficiary.details.walletDetails?.network || ''
        }
      }
      this.$store.dispatch('terminal/usedBeneficiary', true)
      this.$store.dispatch('terminal/beneficiaryID', beneficiary.id)
      this.$store.dispatch('terminal/setRecipientInformation', details)
      this.$emit('toReview')
      this.handleClose()
    },
    handleClose () {
      this.$emit('closeDrawer')
    },
    parseNetworkName (networkType) {
      return ParseNetworkName(networkType)
    },
    maskWalletAddress (walletAddress) {
      return MaskWalletAddressTwo(walletAddress)
    }
  }
}
</script>
<style scoped>
>>> .el-drawer__header > :first-child {
  font-size: 1.1rem;
  color: var(--foreground-default);
  font-weight: 600;
}
>>> .el-drawer__header {
  color: var(--foreground-default);
  font-size: 1.2rem;
  padding-bottom: 1rem;
  padding-top: 2rem;
  padding-left: 1.4rem;
  font-weight: 700;
  border-bottom: 1px solid var(--border-main);
}
>>> .el-drawer {
  background: var(--background-canvas) !important;
}
>>> .el-input__inner {
  background: var(--input-background);
  color: var(--input-text);
  padding-left: 2.8rem;
}
>>> .el-input__placeholder {
  color: var(--input-placeholder);
}
</style>
